// client/src/components/shared/CollapsibleCard.js — home-screen cards that shrink to one
// line on phones (owner: "like 1 line of info until you open the card").
//
// Below 640px (Tailwind `sm`) a card is a single tappable row: icon + title + short summary
// + chevron; tapping (or Enter/Space — it is a real <button>) opens the card's normal
// content underneath. At 640px and up the children render exactly as before, untouched.
// Each card's open/closed choice is remembered per `id` in localStorage; with nothing saved
// a card starts collapsed unless `defaultOpen`. <ExpandCollapseAll /> opens/closes every
// card on the screen at once (phones only).
import React, { createContext, useContext, useEffect, useState } from 'react';
import { readCardOpen, writeCardOpen, initialCardOpen, hideInnerHeading } from '../../utils/collapsibleCards';

// True inside a card that is open on a phone: the tappable row above already shows the
// title, so the card's own heading (<CardHeading>) steps aside instead of repeating it.
const CardHeadingHidden = createContext(false);

/** A card's own inner heading; hidden while the card is open as a one-line phone card. */
export function CardHeading({ as: Tag = 'h2', className, children }) {
    if (useContext(CardHeadingHidden)) return null;
    return <Tag className={className}>{children}</Tag>;
}

const PHONE_QUERY = '(max-width: 639.98px)';
const SET_ALL_EVENT = 'brec-cards-set-all';

const matchPhone = () => {
    try { return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches; } catch { return false; }
};

export function useIsPhone() {
    const [phone, setPhone] = useState(matchPhone);
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mq = window.matchMedia(PHONE_QUERY);
        const on = () => setPhone(mq.matches);
        on();
        if (mq.addEventListener) { mq.addEventListener('change', on); return () => mq.removeEventListener('change', on); }
        mq.addListener(on);
        return () => mq.removeListener(on);
    }, []);
    return phone;
}

export function setAllCards(open) {
    try { window.dispatchEvent(new CustomEvent(SET_ALL_EVENT, { detail: { open } })); } catch { /* old browser */ }
}

const Chevron = ({ open }) => (
    <svg className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
         viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
);

export default function CollapsibleCard({ id, title, summary, icon, defaultOpen = false, tone, className = '', children }) {
    const phone = useIsPhone();
    const [open, setOpen] = useState(() => initialCardOpen(readCardOpen(id), defaultOpen));

    // A card flagged urgent after its data arrives opens, unless the user chose otherwise.
    useEffect(() => {
        if (defaultOpen && readCardOpen(id) === null) setOpen(true);
    }, [defaultOpen, id]);

    useEffect(() => {
        const on = (e) => { const v = !!e.detail?.open; setOpen(v); writeCardOpen(id, v); };
        window.addEventListener(SET_ALL_EVENT, on);
        return () => window.removeEventListener(SET_ALL_EVENT, on);
    }, [id]);

    if (!phone) return <>{children}</>;

    const panelId = `card-panel-${id}`;
    const toggle = () => setOpen((o) => { writeCardOpen(id, !o); return !o; });
    return (
        <div className={className}>
            <button type="button" onClick={toggle} aria-expanded={open} aria-controls={panelId}
                    className="flex w-full items-center gap-2 rounded-lg bg-white px-3 py-2.5 text-left shadow
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400">
                {icon && <span className="shrink-0 text-slate-500" aria-hidden="true">{icon}</span>}
                <span className="truncate text-sm font-semibold text-slate-800">{title}</span>
                {summary ? <span className={`ml-auto truncate pl-2 text-xs ${tone || 'text-slate-500'}`}>{summary}</span> : <span className="ml-auto" />}
                <Chevron open={open} />
            </button>
            <div id={panelId} hidden={!open} className="mt-2">
                {open && <CardHeadingHidden.Provider value={hideInnerHeading(phone, open)}>{children}</CardHeadingHidden.Provider>}
            </div>
        </div>
    );
}

export function ExpandCollapseAll({ className = '' }) {
    const phone = useIsPhone();
    if (!phone) return null;
    return (
        <div className={`flex justify-end gap-3 text-xs ${className}`}>
            <button type="button" className="text-indigo-700 underline" onClick={() => setAllCards(true)}>Expand all</button>
            <button type="button" className="text-indigo-700 underline" onClick={() => setAllCards(false)}>Collapse all</button>
        </div>
    );
}
